import type { ChatToolCall } from '../api/domains/chatbot.api'

/**
 * Assistant tools that change a user or a group on the server. When a finished turn carries one, the
 * Settings → Users and Groups pages re-read: `apps/console` has no query cache, so this module-level
 * signal stands in for it (the same shape as capacity-refresh.ts).
 */
export const SETTINGS_WRITE_TOOLS: ReadonlySet<string> = new Set([
  'change_user_role',
  'suspend_user',
  'resume_user',
  'create_group',
  'update_group',
  'delete_group',
  'add_group_members',
  'remove_group_members',
])

export interface SettingsChange {
  /** The groups the turn changed (or may have), so a form open on one of them never saves over it. */
  groupIds: string[]
}

type Listener = (change: SettingsChange) => void

const listeners = new Set<Listener>()

export function subscribeSettingsRefresh(listener: Listener): () => void {
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
  }
}

/** The group a write names: `group_id` on success, `group` (the gate's ids) on `unknown_outcome`. */
function groupIdOf(r: { group_id?: unknown; group?: unknown }): string | null {
  if (typeof r.group_id === 'string' && r.group_id) return r.group_id
  if (typeof r.group === 'string' && r.group) return r.group

  return null
}

/**
 * A write that succeeded changed something, unless it says `changed: false` (the values were already
 * current, so nothing moved). So did one that came back `unknown_outcome`: the request may well have
 * landed, so the pages re-read rather than keep showing the old state.
 */
export function announceSettingsWrites(toolCalls: readonly ChatToolCall[]): void {
  let changed = false
  const groupIds: string[] = []

  for (const call of toolCalls) {
    if (!SETTINGS_WRITE_TOOLS.has(call.name)) continue
    const r = call.result as
      | { success?: unknown; changed?: unknown; error?: unknown; group_id?: unknown; group?: unknown }
      | null

    if (!r || typeof r !== 'object') continue
    const landed = r.success === true ? r.changed !== false : r.error === 'unknown_outcome'
    if (!landed) continue

    changed = true
    const groupId = groupIdOf(r)
    if (groupId && !groupIds.includes(groupId)) groupIds.push(groupId)
  }

  if (!changed) return

  for (const listener of [...listeners]) {
    try {
      listener({ groupIds })
    } catch {
      // One page's failed refresh must not stop the others.
    }
  }
}
